import type { FC } from 'react';
import React, {
	Fragment,
	useCallback,
	useState,
	useContext,
	useMemo,
	useEffect,
	useRef,
} from 'react';
import type { ApolloQueryResult } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { Box } from '@atlaskit/primitives/compiled/box';

import { fg } from '@confluence/feature-gating';
import { FEED_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { InfiniteScroll } from '@confluence/infinite-scroll';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { GenericFeedError, PaginationFeedError } from '../FeedError/index.ts';
import { EndOfFeed, EndOfFeedHomeBanner } from '../EndOfFeed/index.ts';
import { EndOfFollowingFeed } from '../EndOfFollowingFeed/EndOfFollowingFeed.tsx';
import { EndOfPopularFeed } from '../EndOfPopularFeed/EndOfPopularFeed.tsx';
import { FeedType } from '../useFeedTypeSelector.ts';
import { isExpectedApolloError } from '../expectedGraphqlErrorHelpers.ts';
import { HOME_OVERVIEW_FEED_PAGE_SEGMENT } from '../perf.config.ts';

import { LoadingSkeleton } from './FeedCard/index.ts';

type Props = {
	loading: boolean;
	firstPageError?: ApolloErrorInterop;
	networkStatus: NetworkStatus;
	endCursor?: string;
	hasNextPage?: boolean;
	loadNextPage: () => Promise<ApolloQueryResult<any> | undefined>;
	openActivityFilterModal?: () => void;
	feedType: FeedType;
	hasResults?: boolean;
	children?: React.ReactNode;
	hasFollowedAnyone?: boolean;
};

export const FeedCardsWrapper: FC<Props> = ({
	loading,
	firstPageError,
	networkStatus,
	endCursor,
	hasNextPage,
	loadNextPage,
	openActivityFilterModal,
	feedType,
	hasResults = true,
	children,
	hasFollowedAnyone,
}) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const experienceTrackerStopAttributes = useMemo(
		() => ({
			feedType: feedType.toLowerCase(),
		}),
		[feedType],
	);

	const [nextPageError, setNextPageError] = useState<ApolloErrorInterop | null>();

	const error = firstPageError || nextPageError;
	const isExpectedError = Boolean(error && isExpectedApolloError(error));

	const { cloudId, orgId } = useSessionData();

	const homeHref = `https://home.atlassian.com/o/${orgId}/discover/?cloudId=${cloudId}`;

	if (error && isExpectedError) {
		markErrorAsHandled(error);
	}

	const handlePagination = useCallback(async () => {
		try {
			if (!loading && hasNextPage) {
				experienceTracker.start({
					name: FEED_EXPERIENCE,
					attributes: {
						pagination: true,
					},
				});
				await loadNextPage();
				setNextPageError(null);
			}
		} catch (e) {
			const apolloError = new ApolloErrorInterop({
				errorMessage: 'Feed cards failed to fetch more data',
				graphQLErrors: e,
			});
			setNextPageError(apolloError);
		}
	}, [loading, hasNextPage, loadNextPage, setNextPageError, experienceTracker]);

	const isFirstRenderAfterSSR = useIsFirstRenderAfterSSR();

	const renderPagination = () => {
		if (!hasNextPage) {
			if (feedType === FeedType.POPULAR) {
				return <EndOfPopularFeed />;
			} else if (feedType === FeedType.FOLLOWING) {
				return (
					<EndOfFollowingFeed
						openActivityFilterModal={openActivityFilterModal}
						hasFollowedAnyone={hasFollowedAnyone}
						hasResults={hasResults}
					/>
				);
			}
			return <EndOfFeed openActivityFilterModal={openActivityFilterModal} />;
		}

		return (
			<InfiniteScroll
				onScrollTo={handlePagination}
				cursor={endCursor}
				observerOptions={{ root: null, rootMargin: '1000px' }}
			>
				<LoadingSkeleton useEntranceAnimation={!isFirstRenderAfterSSR} />
			</InfiniteScroll>
		);
	};

	const renderEndOfFeed = () => {
		if (error && !isExpectedError) {
			return (
				<ErrorDisplay error={error}>
					<ExperienceFailure
						name={FEED_EXPERIENCE}
						error={error}
						attributes={experienceTrackerStopAttributes}
					/>
					{nextPageError ? (
						<PaginationFeedError refetch={handlePagination} />
					) : (
						<GenericFeedError />
					)}
				</ErrorDisplay>
			);
		}

		return (
			<Fragment>
				<ExperienceSuccess
					name={FEED_EXPERIENCE}
					key={endCursor}
					attributes={experienceTrackerStopAttributes}
				/>
				<PageSegmentLoadEnd metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
				{renderPagination()}
			</Fragment>
		);
	};

	const alreadyLoaded = useAlreadyLoaded(loading);

	if (
		loading &&
		(networkStatus === NetworkStatus.refetch ||
			(networkStatus === NetworkStatus.loading && !alreadyLoaded))
	) {
		return <LoadingSkeleton useEntranceAnimation={!isFirstRenderAfterSSR} />;
	}

	const isInPopularTypeFeed =
		feedType === FeedType.POPULAR ||
		feedType === FeedType.FAREWELL ||
		feedType === FeedType.WELCOME;

	// Check if we should show our new empty state instead of the banner
	const shouldShowNewEmptyState = !hasResults && feedType === FeedType.POPULAR;

	return (
		<Fragment>
			<Box
				id={`tabpanel-${feedType.toLowerCase()}`}
				aria-labelledby={`tab-${feedType.toLowerCase()}`}
				role="tabpanel"
				tabIndex={0}
			>
				{children}
			</Box>
			{isInPopularTypeFeed && !shouldShowNewEmptyState && fg('confluence_ai_feeds_home_banner') ? (
				<EndOfFeedHomeBanner linkHref={homeHref} feedType={feedType} />
			) : (
				renderEndOfFeed()
			)}
		</Fragment>
	);
};

const useAlreadyLoaded = (loading: boolean) => {
	const alreadyLoaded = useRef(false);
	useEffect(() => {
		if (!loading) {
			alreadyLoaded.current = true;
		}
	}, [loading]);
	return alreadyLoaded.current;
};

const useIsFirstRenderAfterSSR = () => {
	const isFirstRenderAfterSSR = useRef(Boolean(window.__SSR_RENDERED__));
	useEffect(() => {
		isFirstRenderAfterSSR.current = false;
	}, []);
	return !process.env.SSR && isFirstRenderAfterSSR.current;
};
